import { SaveAgentSkillRequest } from "components/AgentSkills/Models/AgentSkill";

const toolReferenceRegex = /\{\{\s*tool\s*:\s*([^}]*)\s*\}\}/gi;

export const parseAllowedTools = (value?: string | null): string[] =>
    (value ?? "")
        .split(/\s+/)
        .map((tool) => tool.trim())
        .filter(Boolean);

export const formatAllowedTools = (value: readonly string[]): string =>
    value
        .map((tool) => tool.trim())
        .filter(Boolean)
        .join(" ");

const appendUnique = (
    target: string[],
    seen: Set<string>,
    value?: string | null,
) => {
    const trimmed = value?.trim() ?? "";
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;

    seen.add(key);
    target.push(trimmed);
};

export const getReferencedToolNames = (
    skill: SaveAgentSkillRequest,
    availableToolNames: readonly string[] = [],
): string[] => {
    const canonicalNames = new Map(
        availableToolNames.map((name) => [
            name.trim().toLowerCase(),
            name.trim(),
        ]),
    );
    const names: string[] = [];
    const seen = new Set<string>();
    const textBlocks = [
        skill.skillText ?? "",
        ...(skill.files ?? []).map((file) => file.body ?? ""),
    ];

    textBlocks.forEach((text) => {
        toolReferenceRegex.lastIndex = 0;
        let match: RegExpExecArray | null;
        while ((match = toolReferenceRegex.exec(text)) !== null) {
            const referencedName = match[1].trim();
            appendUnique(
                names,
                seen,
                canonicalNames.get(referencedName.toLowerCase()) ??
                    referencedName,
            );
        }
    });

    return names;
};

export const ensureReferencedToolsAllowed = (
    skill: SaveAgentSkillRequest,
    availableToolNames: readonly string[] = [],
): SaveAgentSkillRequest => {
    const allowedTools: string[] = [];
    const seen = new Set<string>();

    parseAllowedTools(skill.frontmatter?.allowedTools).forEach((name) =>
        appendUnique(allowedTools, seen, name),
    );
    getReferencedToolNames(skill, availableToolNames).forEach((name) =>
        appendUnique(allowedTools, seen, name),
    );

    return {
        ...skill,
        frontmatter: {
            ...(skill.frontmatter ?? {}),
            allowedTools: formatAllowedTools(allowedTools),
        },
    };
};
